import { IconButton } from '@mui/material';
import { useTranslation } from 'next-i18next';
import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { MdContentCopy } from 'react-icons/md';
import toast from 'react-hot-toast';
import { LiveStream, Meetingroom } from '../../../../../../interfaces/interfaces';
import { deleteStudio, updateStudio } from '../../../../../../services/services';
import VyButton from '../../../../../common/VyButton';
import { VyConfirm } from '../../../../../common/VyConfirm';
import VyTextField from '../../../../../common/VyTextField';
import VyTooltip from '../../../../../common/VyTooltip';
import VyUpload from '../../../../../common/VyUpload';
import { CopyToClipboard } from '../../../../../common/CopyToClipboard';

interface FormProps {
  name: string;
  studioPassword: string;
}

export const StudioConfiguration = (props: {
  isPresentationStudio?: boolean;
  studio: Meetingroom;
  onUpdate: (studio: Meetingroom | null) => void;
  onClose: () => void;
}) => {
  const { studio, onUpdate, onClose } = props;
  const { t } = useTranslation();
  const { handleSubmit, formState, control } = useForm<FormProps>();

  const [thumbnail, setThumbnail] = useState(studio?.thumbnail ?? null);
  const [loading, setLoading] = useState(false);

  async function handleUpdateStudio(props: FormProps) {
    setLoading(true);
    try {
      await updateStudio(studio._id, { ...props, thumbnail });
      await onUpdate({ ...studio, thumbnail });
      onClose();
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }

  async function handleDeleteStudio() {
    setLoading(true);
    try {
      await deleteStudio(studio._id);
      await onUpdate(null);
      onClose();
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }

  const studioLink = `${process.env.baseURL}/videochat/${props.studio._id}?pw=${props.studio.studioPassword}`;
  return (
    <>
      <form onSubmit={handleSubmit(handleUpdateStudio)}>
        <Controller
          control={control}
          name={`name`}
          defaultValue={studio?.name}
          render={({ field }) => (
            <VyTextField {...field} className="my-2" label={'Name'} style={props.isPresentationStudio ? { visibility: 'hidden', height: 0 } : {}} />
          )}
        />
        <div className="flex items-center">
          <Controller
            control={control}
            name={`studioPassword`}
            defaultValue={studio?.studioPassword}
            render={({ field }) => <VyTextField {...field} name="studioPassword" className="my-2" label={'Passwort'} />}
          />
          <VyTooltip key={`copy-studiolink}`} title={t('account:studio.linkcopy')}>
            <CopyToClipboard text={studio?.studioPassword ?? ''} onCopy={() => toast.success(t('account:studio.linkcopied'))}>
              <IconButton size="large">
                <MdContentCopy />
              </IconButton>
            </CopyToClipboard>
          </VyTooltip>
        </div>
        <VyTooltip title={t('account:studio.participantlink-description')}>
          <div className="flex items-center">
            <VyTextField className="my-2" disabled label={'Teilnehmerlink'} defaultValue={studioLink} />
            <VyTooltip key={`copy-studiolink}`} title={t('account:studio.linkcopy')}>
              <CopyToClipboard text={studioLink} onCopy={() => toast.success(t('account:studio.linkcopied'))}>
                <IconButton size="large">
                  <MdContentCopy />
                </IconButton>
              </CopyToClipboard>
            </VyTooltip>
          </div>
        </VyTooltip>
        <VyTooltip title={t('account:studio.livestream-key-description')} key={`copy-streamkey}`}>
          <div className="flex items-center">
            <VyTextField className="my-2" disabled label={t('account:studio.livestream-key')} defaultValue={(studio?.liveStream as LiveStream)?.streamKey} />
            <VyTooltip key={`copy-studiolink}`} title={t('account:studio.linkcopy')}>
              <CopyToClipboard text={(studio?.liveStream as LiveStream)?.streamKey} onCopy={() => toast.success(t('account:studio.keycopied'))}>
                <IconButton size="large">
                  <MdContentCopy />
                </IconButton>
              </CopyToClipboard>
            </VyTooltip>
          </div>
        </VyTooltip>
        <p>Thumbnail</p>
        <VyUpload
          randomExternalImageButton={false}
          defaultFileId={thumbnail?.toString()}
          onChange={(fileId: string | null) => {
            setThumbnail(fileId);
          }}
        />
        <VyButton
          isSaveButton
          className="my-2 w-full"
          disabled={(thumbnail === studio.thumbnail && !formState.isDirty) || loading}
          type="submit"
          showLoadingIcon={loading}>
          {t('common:save')}
        </VyButton>
        {!props.isPresentationStudio && (
          <VyConfirm onConfirm={handleDeleteStudio}>
            <VyButton className="w-full" error showLoadingIcon={loading} disabled={loading}>
              {t('common:delete')}
            </VyButton>
          </VyConfirm>
        )}
      </form>
    </>
  );
};
